import type { StoreApi } from "zustand";
import type { SandboxDraft, SandboxMachine } from "@/types/sim-sandbox.types";
import { deriveEdgesFromStages } from "../SandboxCanvas/flow-helpers";
import type { SandboxDraftState } from "./useSandboxDraft";

// Ações de estágio/posição do sandbox extraídas do store (mantém useSandboxDraft ≤200
// LOC). Edges são DERIVADAS do modelo em camadas: recomputadas a cada mudança estrutural.
type SetState = StoreApi<SandboxDraftState>["setState"];

export const edgesFor = (m: Record<string, SandboxMachine>, stages: SandboxDraft["stages"]) =>
  deriveEdgesFromStages(Object.values(m), stages);

let stageSeq = 0;
export const nextStageKey = () => `stg-${(stageSeq += 1)}`;

export function createStageActions(set: SetState) {
  return {
    // Move uma máquina para outro estágio (re-deriva edges).
    setStage: (key: string, stageKey: string) =>
      set((s) => {
        const m = s.machines[key];
        if (!m || m.stageKey === stageKey || !s.stages.some((st) => st.key === stageKey)) return s;
        const machines = { ...s.machines, [key]: { ...m, stageKey } };
        return { machines, edges: edgesFor(machines, s.stages), dirty: true };
      }),

    // Cria um estágio ao final; retorna sua key.
    addStage: (name?: string) => {
      const key = nextStageKey();
      set((s) => {
        const order = s.stages.reduce((mx, st) => Math.max(mx, st.order + 1), 0);
        return {
          stages: [...s.stages, { key, name: name ?? `Estágio ${s.stages.length + 1}`, order }],
          dirty: true,
        };
      });
      return key;
    },

    renameStage: (key: string, name: string) =>
      set((s) => ({
        stages: s.stages.map((st) => (st.key === key ? { ...st, name } : st)),
        dirty: true,
      })),

    // Remove um estágio: suas máquinas migram p/ o estágio anterior (ou o próximo). Bloqueia
    // se for o único estágio. Re-normaliza orders + re-deriva edges.
    removeStage: (key: string) =>
      set((s) => {
        const ordered = [...s.stages].sort((a, b) => a.order - b.order);
        if (ordered.length <= 1) return s;
        const idx = ordered.findIndex((st) => st.key === key);
        if (idx < 0) return s;
        const fallback = ordered[idx - 1] ?? ordered[idx + 1];
        if (!fallback) return s;
        const machines = { ...s.machines };
        for (const m of Object.values(machines)) {
          if (m.stageKey === key) machines[m.key] = { ...m, stageKey: fallback.key };
        }
        const stages = ordered.filter((st) => st.key !== key).map((st, i) => ({ ...st, order: i }));
        return { machines, stages, edges: edgesFor(machines, stages), dirty: true };
      }),

    // Reordena o estágio `key` para `newIndex` (re-normaliza orders + re-deriva edges).
    reorderStage: (key: string, newIndex: number) =>
      set((s) => {
        const ordered = [...s.stages].sort((a, b) => a.order - b.order);
        const from = ordered.findIndex((st) => st.key === key);
        if (from < 0) return s;
        const clamped = Math.max(0, Math.min(newIndex, ordered.length - 1));
        const [moved] = ordered.splice(from, 1);
        if (!moved) return s;
        ordered.splice(clamped, 0, moved);
        const stages = ordered.map((st, i) => ({ ...st, order: i }));
        return { stages, edges: edgesFor(s.machines, stages), dirty: true };
      }),
  };
}
